/**
 * Pipeline editor — shared types for the layout primitives shipped in
 * Phase 3 W1.
 *
 * These types describe the data shape callers pass into the layout
 * primitives (`PipelineCanvas`, `BranchedLaneRow`, etc.). Node-level
 * data (the actual blocks inside a stage body) is intentionally out
 * of scope here — that's modeled by sibling primitives (`SystemChip`,
 * `ContainerBlock`, and the upcoming `Block`) and passed in as React
 * children.
 */

import type { ReactNode } from 'react'

/** Where a stage sits horizontally in the canvas row. */
export type StageKind = 'preauth' | 'postauth' | 'postissue' | 'custom'

/**
 * Copy shown in a stage / lane's empty-state — a friendly prompt that
 * replaces the lonely "Add" button when the stage has no admin blocks.
 * Lead with the value the stage delivers, then invite the action.
 */
export interface StageEmptyHint {
  /** Short headline (e.g. "No universal gates yet"). */
  title: string
  /** One-to-two line prompt describing what to add and why. */
  description: string
}

/**
 * Stage column data — describes the look of one stage column inside
 * `PipelineCanvas`. The actual block content is composed by the caller
 * and passed as `children`.
 */
export interface PipelineStageData {
  /** Stable id for keys + selection. */
  id: string
  /** Two-character ordinal shown in the header (e.g. "01", "02A", "03"). */
  num: string
  /** Uppercased stage label (e.g. "Pre-Auth Hooks"). */
  label: string
  /**
   * Right-aligned counter shown next to the label. Numbers or short
   * literals like "signin" / "signup" both work.
   */
  counter?: string | number
  /** One-line description shown under the header. */
  purpose?: string
  /**
   * Accent kind. Drives the top-border accent on the stage card. Use
   * 'custom' + `accentColor` for one-off accents.
   */
  kind?: StageKind
  /**
   * CSS color string. Overrides `kind` when provided. Used as the value
   * of the `--stage-accent` CSS custom property on the stage root.
   */
  accentColor?: string
  /** Stage body content. */
  children?: ReactNode
  /** Footer slot — typically an add-block button. */
  footer?: ReactNode
  /**
   * Copy for the centered empty-state shown when the stage has no
   * children. When omitted a generic prompt is used.
   */
  emptyHint?: StageEmptyHint
}

/**
 * Terminal data — Intake / Allow / Deny circular disk at either end of
 * the canvas. The label sits below the disk.
 */
export interface PipelineTerminalData {
  id: string
  label: string
  /** Optional sub text (two short lines max). Use `\n` to break. */
  sub?: string
  /** Icon centered inside the disk. */
  icon?: ReactNode
  /** Visual tone — affects color of the disk + label. */
  tone?: 'neutral' | 'allow' | 'deny'
  /**
   * When provided, the terminal renders as an interactive `<button>`.
   * The `aria-label` is derived from the terminal's `label` field so
   * screen readers announce the purpose without additional wiring.
   * Without `onClick` the terminal is a static `<div>` — no affordance.
   */
  onClick?: () => void
}

/**
 * Lane data — one of the parallel rows inside `BranchedLaneRow`. The
 * lane uses the same stage-header / stage-body / stage-footer grammar
 * as the main stage column but renders at lane width.
 */
export interface LaneData {
  id: string
  /** Stage ordinal (e.g. "02A", "02B"). */
  num: string
  label: string
  counter?: string | number
  purpose?: string
  /**
   * Lane accent color (CSS color string). Defaults to the existing /
   * new-user accents exported below.
   */
  accentColor: string
  /** Subtle tinted background gradient applied to the lane card. */
  backgroundTint?: string
  children?: ReactNode
  footer?: ReactNode
  /**
   * Copy for the centered empty-state shown when the lane has no
   * children. When omitted a generic prompt is used.
   */
  emptyHint?: StageEmptyHint
}

/**
 * Spine chip data — a small system chip inside the spine track to the
 * right of a lane.
 */
export interface SpineChipData {
  id: string
  title: string
  sub?: string
  tag?: string
  icon?: ReactNode
}

/** Lane + spine pair — one row of the branched lane area. */
export interface BranchedLaneRowData {
  lane: LaneData
  /**
   * Spine chips chained to the right of the lane. May be empty for a
   * lane that needs no system-side processing.
   */
  spineChips: SpineChipData[]
  /**
   * Visual style of the leader stub between lane and first spine chip.
   * `solid` for the new-user row, `dotted` for the existing-user row
   * (the latter signals "this branch skips the heavier spine").
   */
  leaderStyle?: 'solid' | 'dotted'
}

/** Default lane accent — existing-user (matches `--pe-accent-existing`). */
export const EXISTING_LANE_ACCENT = 'var(--pe-accent-existing)'
/** Default lane accent — new-user (matches `--pe-accent-newuser`). */
export const NEWUSER_LANE_ACCENT = 'var(--pe-accent-newuser)'
/** Default fork-out joint color (matches `--pe-accent-identify`). */
export const IDENTIFY_ACCENT = 'var(--pe-accent-identify)'
/** Default fork-in joint color (matches `--pe-accent-postauth`). */
export const POSTAUTH_ACCENT = 'var(--pe-accent-postauth)'
